// Hard rule 1: money is integer paise everywhere. No floats in any money path.
// This module is the ONLY place money math is allowed to happen.

import { RuleViolationError } from '../errors.js';

declare const PaiseBrand: unique symbol;
export type Paise = bigint & { readonly [PaiseBrand]: 'paise' };

const MAX_PAISE = 2n ** 62n; // sanity ceiling — well below BigInt(BIGINT MySQL max)

export function paise(n: bigint | number | string): Paise {
  let v: bigint;
  if (typeof n === 'bigint') v = n;
  else if (typeof n === 'number') {
    if (!Number.isInteger(n)) {
      throw new RuleViolationError('money.integer_only', `paise value must be integer, got ${n}`);
    }
    v = BigInt(n);
  } else {
    if (!/^-?\d+$/.test(n)) {
      throw new RuleViolationError('money.integer_only', `paise value must be integer string, got "${n}"`);
    }
    v = BigInt(n);
  }
  if (v > MAX_PAISE || v < -MAX_PAISE) {
    throw new RuleViolationError('money.overflow', `paise value out of range`);
  }
  return v as Paise;
}

export function rupeesToPaise(rupees: number): Paise {
  if (!Number.isFinite(rupees)) {
    throw new RuleViolationError('money.non_finite', `rupees must be finite`);
  }
  // Multiply by 100 in a way that avoids float drift for typical inputs (2 decimal places).
  const s = rupees.toFixed(2);
  const [whole, frac = '00'] = s.split('.');
  const negative = whole!.startsWith('-');
  const wholeAbs = negative ? whole!.slice(1) : whole!;
  const cents = BigInt(wholeAbs) * 100n + BigInt(frac.padEnd(2, '0').slice(0, 2));
  return paise(negative ? -cents : cents);
}

export function paiseToRupees(p: Paise): number {
  // For display only. Never round-trip through this in a money-critical path.
  const s = (p < 0n ? '-' : '') + absStr(p).padStart(3, '0');
  const whole = s.slice(0, -2);
  const frac = s.slice(-2);
  return Number(`${whole}.${frac}`);
}

function absStr(p: bigint): string {
  return (p < 0n ? -p : p).toString();
}

export function formatInr(p: Paise): string {
  const neg = p < 0n;
  const abs = neg ? -p : p;
  const wholeStr = (abs / 100n).toString();
  const fracStr = (abs % 100n).toString().padStart(2, '0');
  // Indian grouping: last 3 digits, then groups of 2.
  const grouped = groupIndian(wholeStr);
  return `${neg ? '-' : ''}₹${grouped}.${fracStr}`;
}

function groupIndian(s: string): string {
  if (s.length <= 3) return s;
  const last3 = s.slice(-3);
  const rest = s.slice(0, -3);
  const parts: string[] = [];
  let r = rest;
  while (r.length > 2) {
    parts.unshift(r.slice(-2));
    r = r.slice(0, -2);
  }
  if (r.length > 0) parts.unshift(r);
  return parts.join(',') + ',' + last3;
}

export function add(a: Paise, b: Paise): Paise {
  return paise(a + b);
}

export function sub(a: Paise, b: Paise): Paise {
  return paise(a - b);
}

export function sum(items: readonly Paise[]): Paise {
  let acc = 0n;
  for (const it of items) acc += it;
  return paise(acc);
}

/** Multiply paise by a rate expressed in basis-points-of-percent (bpp) — 100 = 1%. Rounds half-even to nearest paise. */
export function applyRateBpp(base: Paise, bpp: number): Paise {
  if (!Number.isInteger(bpp) || bpp < 0 || bpp > 10_000_000) {
    throw new RuleViolationError('money.bad_rate', `rate must be an integer 0..10_000_000 bpp, got ${bpp}`);
  }
  const num = base * BigInt(bpp);
  const denom = 10_000n;
  // Half-even (banker's) rounding.
  const q = num / denom;
  const r = num % denom;
  const twiceR = r * 2n;
  if (twiceR < denom) return paise(q);
  if (twiceR > denom) return paise(q + (num < 0n ? -1n : 1n));
  // exact half — round to even
  return paise(q % 2n === 0n ? q : q + (num < 0n ? -1n : 1n));
}

export function isZero(p: Paise): boolean {
  return p === 0n;
}

export function netsToZero(items: readonly Paise[]): boolean {
  let acc = 0n;
  for (const it of items) acc += it;
  return acc === 0n;
}
